import {useState, useEffect} from 'react';
import { Box, Typography, Button } from '@mui/material';
import { isMinLength2, isValidEmail, isValidPhoneNumber } from '../../../lib/validate';
import ValidatedTextField from '../../components/ValidatedTextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {saveOrder, OrderFormProps, fetchOrder} from '../../lib/order';
import { useApp } from '../../components/AppContext';
import {ContactInformationRequest, ContactInformation} from '../../types/order';
import { APIHOST } from '../../common'
import { getToken } from '../../components/auth/AuthContext';

export interface ContactInformationSaveData {
  orderId: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactCell: string;
}

export default function ContactInformationForm({
  orderId,
  back,
  onSave,
  type
}: OrderFormProps) {
 
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactCell, setContactCell] = useState('');
  const { setSnackbarInput } = useApp();

  if (!orderId) {
    return <Typography variant="h6" color="error">No order ID provided</Typography>;
  }
   const isFormValid = () => {
    return isMinLength2(contactFirstName) &&
           isMinLength2(contactLastName) &&
           isValidEmail(contactEmail) &&
           isValidPhoneNumber(contactCell);
  };

  const handleSaveOrderClick = () => {
    if (!orderId) return;

    const saveData: ContactInformation = {
      contactFirstName,
      contactLastName,
      contactEmail,
      contactCell
    };

    fetch(`${APIHOST}/api/orders/${orderId}/contact-information`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getToken()}`
      },
      body: JSON.stringify({ data: saveData } as ContactInformationRequest)
    })
    .then(response => response.json())
    .then(success => {
      if (success) {
        setSnackbarInput({ message: 'Contact information saved successfully', severity: 'success' });
        if (onSave) onSave();
      } else {
        setSnackbarInput({ message: 'Failed to save contact information', severity: 'error' });
        console.error('Failed to save order');
      }
    })
    .catch(error => {
      setSnackbarInput({ message: 'Error saving contact information', severity: 'error' });
      console.error('Error saving order:', error);
    });
  };

  useEffect(() => {
    // Fetch order details on mount
    fetchOrder(orderId)
    .then(data => {
      if (!data) {
        console.error('No order data found');
        return;
      }
      console.log('Fetched order data:', data);
      setContactFirstName(data.contactFirstName || '');
      setContactLastName(data.contactLastName || '');
      setContactEmail(data.contactEmail || '');
      setContactCell(data.contactCell || '');
      })
    .catch(error => {
      console.error('Error fetching order:', error);
    });

  }, [orderId]);
  
  return (
    <Box>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '400px' }}>
     
          <ValidatedTextField
            validator={isMinLength2}
            label="First Name"
            value={contactFirstName}
            onChange={(e) => setContactFirstName(e.target.value)}
            variant="outlined"
            size="small"
            required
          />
        
          <ValidatedTextField
            validator={isMinLength2}
            fullWidth
            label="Last Name"
            value={contactLastName}
            onChange={(e) => setContactLastName(e.target.value)}
            variant="outlined"
            size="small"
            required
          />
      
          <ValidatedTextField
            validator={isValidEmail}
            fullWidth
            label="Email Address"
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            variant="outlined"
            size="small"
            required
            helperText={isValidEmail(contactEmail) === false ? 'Please enter a valid email address' : ''}
          />
    
          <ValidatedTextField
            validator={isValidPhoneNumber}
            fullWidth
            label="Mobile Phone"
            type="tel"
            value={contactCell}
            onChange={(e) => setContactCell(e.target.value)}
            variant="outlined"
            size="small"
            required
            placeholder="(555) 123-4567"
            helperText={isValidPhoneNumber(contactCell) === false ? 'Please enter a valid 10-digit phone number' : ''}
          />
        </Box>
        <Box>
          {back && <Button
            color="primary"
            size="small"
            startIcon={<ArrowBackIcon fontSize="small" />}
            sx={{px: 4, py: 0.5, mt: 2}}
            onClick={back} 
            disabled={!back} >
            Back
          </Button>}
          <Button
            color="primary"
            size="small"
            variant="contained"
            startIcon={<ArrowForwardIcon fontSize="small" />}
            sx={{px: 4, py: 0.5, mt: 2}}
            onClick={handleSaveOrderClick}
            disabled={!isFormValid()}
          >
            {type === 'step' ? 'Next' : 'Save'}
          </Button>
        </Box>
    </Box>
  );
}